import { addWildcardToRoutes, useNavigate, withOutlet, withRouteId } from '@melio/ar-domain';
import { forwardRef } from '@melio/platform-utils';
import { Route, Routes } from 'react-router-dom';

import {
  CreateCustomerActivity,
  CustomersTabActivity,
  UpdateCustomerActivity as _UpdateCustomerActivity,
} from '../../activities';

const UpdateCustomerActivity = withRouteId(_UpdateCustomerActivity, 'customerId');

export type CustomersTableFlowProps = {
  onError?: ErrorFunction;
  onCreateInvoice: (customerId: string) => unknown;
  onDisplayCustomerInvoices: (searchTern: string) => unknown;
};

export const CustomersTableFlow = forwardRef<CustomersTableFlowProps>(
  ({ onCreateInvoice, onDisplayCustomerInvoices, onError, ...props }, ref) => {
    const { Paths, goToTable, goToCreateCustomer, goToEditCustomer } = useCustomersTableFlow({
      withSearchparams: true,
    });

    return (
      <Routes>
        <Route
          path={Paths.Home}
          element={withOutlet(
            <CustomersTabActivity
              onCreateCustomer={goToCreateCustomer}
              onEditCustomer={goToEditCustomer}
              onCreateInvoice={onCreateInvoice}
              onDisplayInvoices={onDisplayCustomerInvoices}
              {...props}
              ref={ref}
            />
          )}
        >
          <Route
            path={Paths.CreateCustomer}
            element={<CreateCustomerActivity isOpen onClose={goToTable} onDone={goToTable} onError={onError} />}
          />
          <Route
            path={Paths.EditCustomer}
            element={<UpdateCustomerActivity isOpen onClose={goToTable} onDone={goToTable} onError={onError} />}
          />
        </Route>
      </Routes>
    );
  }
);

enum Paths {
  Home = '',
  CreateCustomer = 'create',
  EditCustomer = ':id/edit',
}
export const useCustomersTableFlow = (...args: Parameters<typeof useNavigate<Paths>>) => {
  const navigate = useNavigate<Paths>(...args);
  const createNavigateWithId = (path: Paths) => (id: string) => navigate(path, { pathParams: { id } });

  const goToCreateCustomer = () => navigate(Paths.CreateCustomer);
  const goToEditCustomer = createNavigateWithId(Paths.EditCustomer);
  const goToTable = () => navigate(Paths.Home);

  return {
    RedirectPaths: Paths,
    Paths: addWildcardToRoutes(Paths),
    goToCreateCustomer,
    goToEditCustomer,
    goToTable,
  };
};
